/**
 * CMS-08 — the home editorial builder: three panes (rows · items of the selected row · live TV
 * preview) plus "Publicar" and "Descartar borrador" (docs/06 §2.7).
 *
 * Editing is local and **auto-saved** as a draft (`PUT /cms/home`, debounced): the preview is
 * server-rendered from the saved draft (`GET /cms/home/preview`), so what the pane shows is
 * literally what a TV would render. Publishing (`POST /cms/home/publish`, audited
 * `home.publish`) copies the draft over the live layout; the apps see nothing until then.
 */
import {
  type CmsHomePreviewResponse,
  type CmsHomeResponse,
  type CmsHomeRowDTO,
  type CmsPlaylistsResponse,
  type CmsSongsResponse,
  HOME_MAX_ITEMS_PER_ROW,
  HOME_MAX_ROWS,
  type HomeItemRefType,
  type HomeRowType,
} from '@akpl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { EntitySearch, type SearchCandidate } from '../components/editorial/EntitySearch';
import { OrderedList, swatch } from '../components/editorial/OrderedList';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Input } from '../components/ui/field';
import { Badge, Panel, PanelHeader } from '../components/ui/panel';
import { useToast } from '../components/ui/toast';
import { useT } from '../i18n';
import { formatDateTime } from '../lib/format';
import { appendUnique, move, removeAt } from '../lib/order';
import { useOperatorName } from '../lib/useOperatorName';

/**
 * The preview is the only thing in the console that pulls in `@akpl/ui-tv` and its spatial
 * navigation. It is loaded on demand so that chunk never reaches the main bundle and never
 * binds a key listener on the other fourteen routes (QA 2026-09-15 UX-500).
 */
const TvPreview = lazy(() => import('../components/editorial/TvPreview'));

/** Debounce of the draft auto-save: long enough to batch a drag, short enough to feel live. */
export const AUTOSAVE_MS = 800;

export const ROW_TYPES: HomeRowType[] = ['heroes', 'playlists', 'songs'];

const ITEM_TYPE: Record<HomeRowType, HomeItemRefType> = {
  heroes: 'hero',
  playlists: 'playlist',
  songs: 'song',
};

interface DraftItem {
  refType: HomeItemRefType;
  refId: string;
  title: string;
  subtitle: string;
  count: number | null;
  imageUrl: string | null;
  published: boolean;
}

interface DraftRow {
  /** Local key: the layout is rewritten on every save, so server ids are not stable. */
  key: string;
  type: HomeRowType;
  title: { en: string; es: string };
  items: DraftItem[];
}

let keySeq = 0;
const nextKey = () => `row-${++keySeq}`;

export function toDraftRows(rows: CmsHomeRowDTO[]): DraftRow[] {
  return rows.map((row) => ({
    key: nextKey(),
    type: row.type,
    title: { en: row.title.en, es: row.title.es },
    items: row.items.map((item) => ({
      refType: item.refType,
      refId: item.refId,
      title: item.title,
      subtitle: item.subtitle,
      count: item.count,
      imageUrl: item.imageUrl,
      published: item.published,
    })),
  }));
}

/** The `PUT /cms/home` body — order is array order (docs/06 §2.7). */
export function toPutBody(rows: DraftRow[]) {
  return {
    rows: rows.map((row) => ({
      type: row.type,
      title: row.title,
      items: row.items.map((item) => ({ refType: item.refType, refId: item.refId })),
    })),
  };
}

function candidateToItem(refType: HomeItemRefType, candidate: SearchCandidate): DraftItem {
  return {
    refType,
    refId: candidate.id,
    title: candidate.title,
    subtitle: candidate.subtitle ?? '',
    count: null,
    imageUrl: candidate.imageUrl ?? null,
    published: !candidate.tag,
  };
}

function HomeBuilder({ initial, canEdit }: { initial: CmsHomeResponse; canEdit: boolean }) {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const publishedByName = useOperatorName(initial.publishedBy);

  const [rows, setRows] = useState<DraftRow[]>(() => toDraftRows(initial.rows));
  const [selectedKey, setSelectedKey] = useState<string | null>(
    () => toDraftRows(initial.rows)[0]?.key ?? null,
  );
  const [dirty, setDirty] = useState(initial.dirty);
  const [dragRow, setDragRow] = useState<number | null>(null);
  /** An edit is waiting out the auto-save debounce: unsaved, exactly like an in-flight PUT. */
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The first row is selected on mount; `selectedKey` may point at a row that was removed.
  const selected = rows.find((row) => row.key === selectedKey) ?? rows[0] ?? null;

  const preview = useQuery<CmsHomePreviewResponse>({
    queryKey: ['cms', 'home', 'preview'],
    queryFn: () => api.cmsHomePreview(),
  });

  const saveDraft = useMutation({
    mutationFn: (next: DraftRow[]) => api.cmsHomePut({ body: toPutBody(next) }),
    onSuccess: (saved) => {
      setDirty(saved.dirty);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'home', 'preview'] });
    },
    onError: () => setError(t('cms:home.saveFailed')),
  });

  const confirm = useConfirm();
  const toast = useToast();

  const publish = useMutation({
    mutationFn: () => api.cmsHomePublish(),
    onSuccess: () => {
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'home'] });
    },
    onError: () => setError(t('cms:home.publishFailed')),
  });

  const discard = useMutation({
    mutationFn: () => api.cmsHomeDraftDelete(),
    onSuccess: (home) => {
      const restored = toDraftRows(home.rows);
      setRows(restored);
      setSelectedKey(restored[0]?.key ?? null);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'home', 'preview'] });
    },
    onError: () => setError(t('cms:home.saveFailed')),
  });

  /**
   * Publishing replaces the live Home on every TV and phone; discarding throws the draft away.
   * Both ask first (UX-211) — publish on its primary, discard on Cancel.
   */
  const onPublish = async () => {
    const ok = await confirm({
      title: t('cms:confirm.publishHome.title'),
      body: t('cms:confirm.publishHome.body'),
      confirmLabel: t('cms:confirm.publishHome.confirm'),
      tone: 'primary',
    });
    if (ok) publish.mutate();
  };

  const onDiscard = async () => {
    const ok = await confirm({
      title: t('cms:confirm.discardHome.title'),
      body: t('cms:confirm.discardHome.body'),
      confirmLabel: t('cms:confirm.discardHome.confirm'),
      tone: 'danger',
    });
    if (ok) discard.mutate();
  };

  /** Every edit goes through here: local state first, then a debounced draft save. */
  const apply = (next: DraftRow[]) => {
    setRows(next);
    setDirty(true);
    if (!canEdit) return;
    if (timer.current) clearTimeout(timer.current);
    setQueued(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setQueued(false);
      saveDraft.mutate(next);
    }, AUTOSAVE_MS);
  };

  /**
   * QA 2026-09-15 **UX-567** — `PUT /cms/home` answers in about four seconds and the page was
   * not guarded while it ran: reloading 2.5 s after pressing «Deshacer» left the draft in a
   * state that was neither before nor after (three rows with 0 items each), and the only way
   * out was «Descartar borrador», which throws every other draft change away with it.
   *
   * While an edit is queued or in flight the page therefore (a) says so in the header, (b) asks
   * before an in-app navigation and (c) arms `beforeunload` for a reload or a closed tab. The
   * API-side half of the fix made the save itself a couple of round trips instead of ten.
   */
  const savePending = queued || saveDraft.isPending;
  const pendingRef = useRef(savePending);
  useEffect(() => {
    pendingRef.current = savePending;
  }, [savePending]);

  useBlocker({
    // `shouldBlockFn` may answer asynchronously, so the shared confirm dialog is the prompt —
    // the console never uses `window.confirm` (UX-211).
    shouldBlockFn: async () => {
      if (!pendingRef.current) return false;
      return !(await confirm({
        title: t('cms:confirm.leaveWhileSaving.title'),
        body: t('cms:confirm.leaveWhileSaving.body'),
        confirmLabel: t('cms:confirm.leaveWhileSaving.confirm'),
        tone: 'danger',
      }));
    },
    enableBeforeUnload: () => pendingRef.current,
  });

  const updateRow = (key: string, patch: Partial<DraftRow>) =>
    apply(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const rowName = (row: DraftRow) => row.title.es || row.title.en || row.type;

  /**
   * QA 2026-09-15 **UX-534** — «Eliminar fila» was the one destructive action in the console
   * with no gate: one click dropped the row *and every item in it* and `PUT` the draft
   * immediately, while thirteen lesser actions (including unpublishing a single song, which has
   * an undo) all asked first. The only walk-back was «Descartar borrador», which throws away
   * every other edit with it.
   *
   * Removing a row or an item now (a) asks with the shared `alertdialog` and (b) leaves a
   * «Deshacer» toast that puts the exact previous draft back. The draft is what changes: nothing
   * a room can see moves until «Publicar el Home», which still asks on its own.
   */
  const undoable = (previous: DraftRow[], message: string) =>
    toast({
      message,
      tone: 'danger',
      action: { label: t('cms:undo.action'), onAction: () => apply(previous) },
    });

  const onRemoveRow = async (index: number) => {
    const row = rows[index];
    if (!row) return;
    const name = rowName(row);
    const ok = await confirm({
      title: t('cms:confirm.removeHomeRow.title', { name }),
      body: t('cms:confirm.removeHomeRow.body', { count: row.items.length }),
      confirmLabel: t('cms:confirm.removeHomeRow.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    const previous = rows;
    apply(removeAt(rows, index));
    undoable(previous, t('cms:undo.homeRowRemoved', { name }));
  };

  const onRemoveItem = async (row: DraftRow, index: number) => {
    const item = row.items[index];
    if (!item) return;
    const name = item.title || item.refId;
    const ok = await confirm({
      title: t('cms:confirm.removeHomeItem.title', { name }),
      body: t('cms:confirm.removeHomeItem.body'),
      confirmLabel: t('cms:confirm.removeHomeItem.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    const previous = rows;
    updateRow(row.key, { items: removeAt(row.items, index) });
    undoable(previous, t('cms:undo.homeItemRemoved', { name }));
  };

  const addRow = (type: HomeRowType) => {
    if (rows.length >= HOME_MAX_ROWS) return;
    const row: DraftRow = {
      key: nextKey(),
      type,
      title: {
        en: t(`cms:home.defaultTitle_${type}`, { lng: 'en' }),
        es: t(`cms:home.defaultTitle_${type}`, { lng: 'es' }),
      },
      items: [],
    };
    apply([...rows, row]);
    setSelectedKey(row.key);
  };

  const searchItems = async (type: HomeRowType, term: string): Promise<SearchCandidate[]> => {
    if (type === 'heroes') {
      const page = await api.cmsHeroes({ query: { q: term, pageSize: 8 } });
      return page.items.map((hero) => ({
        id: hero.id,
        title: hero.name,
        subtitle: hero.refTitle,
        imageUrl: hero.posterTvUrl,
        tag: hero.status === 'published' && hero.refPublished ? undefined : t('cms:status.draft'),
      }));
    }
    if (type === 'playlists') {
      const page: CmsPlaylistsResponse = await api.cmsPlaylists({
        query: { q: term, pageSize: 8 },
      });
      return page.items.map((playlist) => ({
        id: playlist.id,
        title: playlist.name.es || playlist.name.en,
        subtitle: t('cms:playlists.songsCount', { count: playlist.total }),
        imageUrl: playlist.imageUrl,
        tag: playlist.status === 'published' ? undefined : t('cms:status.draft'),
      }));
    }
    const page: CmsSongsResponse = await api.cmsSongs({ query: { q: term, pageSize: 8 } });
    return page.items.map((song) => ({
      id: song.id,
      title: song.title,
      subtitle: song.artists.map((artist) => artist.name).join(', '),
      imageUrl: song.imageUrl,
      tag: song.status === 'published' ? undefined : t('cms:status.draft'),
    }));
  };

  const rowSubtitle = (row: DraftRow) =>
    t(`cms:home.rowCount_${row.type}`, { count: row.items.length });

  const status = savePending
    ? t('cms:home.savingDraft')
    : dirty
      ? t('cms:home.unpublished')
      : t('cms:home.upToDate');

  return (
    <>
      <PageHeader
        title={t('cms:home.title')}
        subtitle={
          initial.publishedAt
            ? t('cms:home.lastPublishedBy', {
                time: formatDateTime(initial.publishedAt, locale),
                who: publishedByName,
              })
            : t('cms:home.neverPublished')
        }
        actions={
          <span className="flex items-center gap-2">
            {/* `status`, so the switch to «Guardando el borrador…» and back is announced. */}
            <Badge
              role="status"
              aria-live="polite"
              tone={dirty || savePending ? 'warn' : 'ok'}
              data-testid="home-status"
            >
              {status}
            </Badge>
            {canEdit && (
              <>
                <Button
                  variant="secondary"
                  disabled={!initial.hasDraft && !dirty}
                  onClick={() => void onDiscard()}
                >
                  {t('cms:actions.discardDraft')}
                </Button>
                <Button disabled={publish.isPending} onClick={() => void onPublish()}>
                  {t('cms:home.publish')}
                </Button>
              </>
            )}
          </span>
        }
      />

      <div className="grid grid-cols-1 gap-4 px-6 pb-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_360px]">
        {/* --- rows ------------------------------------------------------------------ */}
        <Panel>
          {/*
            The "+ Hero / + Listas / + Canciones" buttons sit on their own line under the header:
            beside the title they took ~245 px of a ~390 px column at 1440 px and squeezed
            "Filas del Home" and its hint onto 2–3 lines each (UI review 2026-10-05, cms-09).
          */}
          <PanelHeader title={t('cms:home.rows')} subtitle={t('cms:home.rowsHelp')} />
          {canEdit ? (
            <div
              data-testid="home-add-row"
              className="flex flex-wrap items-center gap-1 border-b border-line px-4 py-2"
            >
              {ROW_TYPES.map((type) => (
                <Button key={type} variant="secondary" size="sm" onClick={() => addRow(type)}>
                  + {t(`cms:home.rowType_${type}`)}
                </Button>
              ))}
            </div>
          ) : null}
          <ul className="flex flex-col gap-2 p-3" data-testid="home-rows">
            {rows.length === 0 && (
              <li className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-[12px] text-muted">
                {t('cms:home.noRows')}
              </li>
            )}
            {rows.map((row, index) => (
              <li
                key={row.key}
                draggable={canEdit}
                onDragStart={() => setDragRow(index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragRow !== null) apply(move(rows, dragRow, index));
                  setDragRow(null);
                }}
                onDragEnd={() => setDragRow(null)}
                data-testid={`home-row-${row.key}`}
                className={
                  row.key === selected?.key
                    ? 'rounded-lg border border-pink bg-pink/6 px-3 py-2'
                    : 'rounded-lg border border-line px-3 py-2'
                }
              >
                <button
                  type="button"
                  className="flex w-full items-center gap-2 text-left"
                  onClick={() => setSelectedKey(row.key)}
                >
                  <span aria-hidden="true" className="cursor-grab text-muted">
                    ⠿
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-ink">
                      {row.title.es} / {row.title.en}
                    </span>
                    <span className="mt-1 flex items-center gap-2">
                      <Badge tone={row.type === 'heroes' ? 'pink' : 'neutral'}>
                        {t(`cms:home.rowType_${row.type}`)}
                      </Badge>
                      <span className="text-[12px] text-muted">{rowSubtitle(row)}</span>
                    </span>
                  </span>
                </button>
                {canEdit && row.key === selected?.key && (
                  <div className="mt-2 flex flex-col gap-2 border-t border-line pt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        aria-label={t('cms:home.rowTitleEs')}
                        value={row.title.es}
                        onChange={(event) =>
                          updateRow(row.key, { title: { ...row.title, es: event.target.value } })
                        }
                      />
                      <Input
                        aria-label={t('cms:home.rowTitleEn')}
                        value={row.title.en}
                        onChange={(event) =>
                          updateRow(row.key, { title: { ...row.title, en: event.target.value } })
                        }
                      />
                    </div>
                    <div className="flex gap-2">
                      {/* UX-509: the two movers that reorder the rails a room sees were bare
                          glyphs with no accessible name. */}
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={index === 0}
                        aria-label={t('cms:home.moveRowUp', { name: rowName(row) })}
                        onClick={() => apply(move(rows, index, index - 1))}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={index === rows.length - 1}
                        aria-label={t('cms:home.moveRowDown', { name: rowName(row) })}
                        onClick={() => apply(move(rows, index, index + 1))}
                      >
                        ↓
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="ml-auto text-danger"
                        onClick={() => void onRemoveRow(index)}
                      >
                        {t('cms:home.removeRow')}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Panel>

        {/* --- items of the selected row --------------------------------------------- */}
        <Panel>
          <PanelHeader
            title={
              selected
                ? t('cms:home.itemsOf', { title: selected.title.es || selected.title.en })
                : t('cms:home.items')
            }
            subtitle={t('cms:home.itemsHelp')}
            action={selected ? <Badge tone="pink">{selected.items.length}</Badge> : null}
          />
          <div className="flex flex-col gap-3 p-3">
            {!selected && <p className="text-[12px] text-muted">{t('cms:home.selectRow')}</p>}
            {selected && (
              <>
                {canEdit && selected.items.length < HOME_MAX_ITEMS_PER_ROW && (
                  <EntitySearch
                    kind={`home-${selected.type}`}
                    placeholder={t(`cms:home.searchItems_${selected.type}`)}
                    search={(term) => searchItems(selected.type, term)}
                    exclude={selected.items.map((item) => item.refId)}
                    onPick={(candidate) =>
                      updateRow(selected.key, {
                        items: appendUnique(
                          selected.items,
                          candidateToItem(ITEM_TYPE[selected.type], candidate),
                          (a, b) => a.refId === b.refId,
                        ),
                      })
                    }
                  />
                )}
                <OrderedList
                  data-testid="home-items"
                  emptyLabel={t('cms:home.noItems')}
                  disabled={!canEdit}
                  entries={selected.items.map((item) => ({
                    key: item.refId,
                    title: item.title || item.refId,
                    subtitle:
                      item.count === null
                        ? item.subtitle
                        : t('cms:playlists.songsCount', { count: item.count }),
                    imageUrl: item.imageUrl,
                    color: swatch(item.refId),
                    tag: item.published ? undefined : t('cms:home.notPublished'),
                    warn: !item.published,
                  }))}
                  onMove={(from, to) =>
                    updateRow(selected.key, { items: move(selected.items, from, to) })
                  }
                  onRemove={(index) => void onRemoveItem(selected, index)}
                />
                {selected.items.some((item) => !item.published) && (
                  <p className="rounded-lg bg-warn/12 px-3 py-2 text-[12px] text-warn-ink">
                    {t('cms:home.unpublishedItemsWarning')}
                  </p>
                )}
              </>
            )}
          </div>
        </Panel>

        {/* --- live preview ----------------------------------------------------------- */}
        <Panel>
          <PanelHeader
            title={t('cms:home.preview')}
            subtitle={t('cms:home.previewMeta')}
            action={<Badge tone="cyan">{t('cms:home.live')}</Badge>}
          />
          <div className="flex flex-col gap-3 p-3">
            <Suspense
              fallback={
                <div
                  className="rounded-panel bg-[#0b0b14]"
                  style={{ width: 320, height: 180 }}
                  aria-hidden="true"
                />
              }
            >
              <TvPreview
                rows={preview.data?.rows ?? []}
                width={320}
                isLoading={preview.isPending || saveDraft.isPending}
              />
            </Suspense>
            <p className="rounded-lg bg-canvas px-3 py-2 text-[12px] text-muted">
              {t('cms:home.previewHelp')}
            </p>
            {error && (
              <p className="text-[12px] text-danger" role="alert">
                {error}
              </p>
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}

export function HomeEditorial() {
  const { t } = useT();
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');
  const home = useQuery<CmsHomeResponse>({
    queryKey: ['cms', 'home'],
    queryFn: () => api.cmsHome(),
    // The builder owns the draft once it is mounted; only publish/discard reload it.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  if (home.isPending)
    return <PageHeader title={t('cms:home.title')} subtitle={t('common:states.loading')} />;
  if (home.isError || !home.data) {
    return <PageHeader title={t('cms:home.title')} subtitle={t('common:states.error')} />;
  }
  // Remounted only when the server layout is replaced (publish / discard reload the query).
  return <HomeBuilder key={home.dataUpdatedAt} initial={home.data} canEdit={canEdit} />;
}
